import {defineToolRegistryManifest} from '../../types/base-tool-types';
import {z} from 'zod';
import dedent from 'dedent';
import {elementIdSchema, cssSchema} from '../../schemas';
import {createRenderInsertModeSchema} from '../render-insert-mode-schema';
import {RENDER_NESTING_ERROR_PATTERNS} from '../render-nesting-error-patterns';

const toolName = 'render' as const;
const emptyPageGuidance =
  '- **Empty page**: Call read-page-tree first. If its instructions say the current mode cannot add raw structure under Body, do not call render; explain that the user must restore or use a Page Slot with component instances, or switch to Design mode. Otherwise, omit insertTargetElementId — content is added to the page body automatically.';

const innerArgumentZod = createRenderInsertModeSchema({
  baseShape: {
    html: z
      .string()
      .optional()
      .describe(
        dedent`
      # HTML Content

      The HTML markup to render into the DOM.

      ## Restrictions
      - Never render CSS or JavaScript in this field.
      - No <ul>, <ol>, <li> — use <div> instead.
      - Single class per element (e.g. class="card-header").

      ## Requirements
      - **Valid structure**: Use proper HTML element hierarchy
      `
      ),
    css: cssSchema,
    // Optional: Designer canvas ignores this field.
    displayName: z.string().optional().describe(dedent`
    Human-friendly name for the rendered content. Generate it yourself based on
    the element being created (e.g. a hero section you are rendering → "Hero
    Section").
    `),
  },
  insertModeDescription: dedent`
    # Insertion Mode

    How to insert the HTML content into the DOM.

    ## Options
    - **before**: Insert as sibling BEFORE the target element (default). New plain HTML gets new element IDs; re-read before targeting it.
    - **after**: Insert as sibling AFTER the target element. New plain HTML gets new element IDs; re-read before targeting it.
    - **append**: Insert as last child inside the target element (ONLY for plain containers like Body or Div — NEVER target a component). New plain HTML gets new element IDs; re-read before targeting it.
    - **prepend**: Insert as first child inside the target element (ONLY for plain containers — NEVER target a component). New plain HTML gets new element IDs; re-read before targeting it.
    - **replace**: Replace the target element entirely. **MUST set \`insertTargetElementId\`.** The target keeps this ID, but rebuilt child elements get new IDs; re-read before targeting a child.
    - **remove**: Delete the target element. **MUST set \`insertTargetElementId\`.** The target ID becomes invalid after success. NEVER use on \`<wf-component>\` elements unless the user explicitly asks to remove that component.

    ## Decision Logic
    ${emptyPageGuidance}
    - **Existing page**: Use read-page-tree to get element IDs, then position with 'before'/'after' + insertTargetElementId
    - "Add X below Y" → use 'after' with Y's element ID
    - "Add X above Y" → use 'before' with Y's element ID
    - **Move / reorder**: Prefer \`page-automation-public-mcp-compound-elements\` with action \`move_element\` when that tool is available — it reparents the existing element and keeps bindings/styles. Do NOT \`remove\` then re-insert: \`remove\` permanently deletes the instance, and PRESERVE cannot find it afterward. If only \`render\` is available, rewrite the parent/slot children in **one** render call with every instance as PRESERVE (\`<wf-component data-w-id="...">\`) in the new order while those instances are still on the page. Never rebuild moved content as plain HTML.
    - **Default**: Use 'before' when unsure
    - **Retry**: Use 'replace' when user asks to try again
    - **NEVER** use 'append' or 'prepend' targeting a component — this inserts INSIDE the component and breaks it. Use 'before' or 'after' instead.
    - **NEVER** use 'remove' or 'replace' on component instances you just placed — update their props instead
    `,
  insertTargetElementIdSchema: elementIdSchema.describe(
    dedent`
      # Target Element ID

      The element ID to position relative to. Get IDs from read-page-tree output.

      ## Usage
      - **Required for**: 'replace' and 'remove' modes
      - **Recommended for**: 'before' and 'after' modes — positions the new content as a sibling of this element
      - **Default behavior**: If not provided, uses currently selected element

      ## Example: "add X below the Hero"
      Use insertMode: 'after' + insertTargetElementId: <Hero's element ID from read-page-tree>
      `
  ),
});

/**
 * AI models intermittently send `null` for optional string fields
 * (`displayName`, `css`) instead of omitting them. That fails Zod's
 * `z.string().optional()` and surfaces as `toolexecutionerror`.
 *
 * Coerce `null` / blank `displayName` and `css: null` to `undefined` before
 * validation so those calls succeed. `displayName` is optional because the
 * Designer canvas ignores it.
 */
const argumentZod = z.preprocess((input) => {
  if (typeof input !== 'object' || input === null) {
    return input;
  }
  const obj = {...(input as Record<string, unknown>)};
  const rawDisplayName = obj.displayName;
  if (
    rawDisplayName === null ||
    (typeof rawDisplayName === 'string' && rawDisplayName.trim().length === 0)
  ) {
    obj.displayName = undefined;
  }
  if (obj.css === null) {
    obj.css = undefined;
  }
  return obj;
}, innerArgumentZod);

export const renderTool = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Render',
  prettyStatus: {
    executing: 'Rendering...',
    executed: 'Rendered',
    failed: 'Failed to render',
  },
  prettyErrors: {
    fallback: 'Unable to render the content. Please try again.',
    patterns: [
      {
        match: 'Target element with id',
        message: 'Element not found.',
      },
      {
        match: 'Target element not found',
        message: 'Element not found.',
        aiInstruction:
          'The insertTargetElementId you used does not exist on the page — it is stale or was already removed by an earlier step. This was a mistake: do NOT retry with the same id. Call read-page-tree to get the current element IDs, then retry with a valid id. If you were removing/replacing an element that no longer exists, the goal is already met — move on.',
      },
      {
        match: 'inside a component instance',
        message:
          "This element is inside a component and can't be edited directly.",
        aiInstruction:
          'The insertTargetElementId you used belongs to a component internal element (data-w-instance-of), not a targetable data-w-id. Component internals are read-only. Do NOT retry this id. Update the instance using MODIFY mode with prop-* attributes on the <wf-component> tag, or tell the user the content must be edited in the component definition. Call read-page-tree only if you need a different targetable data-w-id.',
      },
      {
        match: 'grid layout identifier',
        message: 'Element not found.',
        aiInstruction:
          'The insertTargetElementId you used is a w-node-* grid layout identifier, not a targetable data-w-id. Do NOT retry with this id. Re-check the read result and use the element data-w-id value instead.',
      },
      {
        match: 'CMS collection identifier',
        message: 'CMS Collection content cannot be edited with render.',
        aiInstruction:
          'The insertTargetElementId you used is a CMS collection identifier (data-w-collection-*), not a targetable data-w-id. Do NOT retry. Tell the user this content is CMS-driven and must be edited in CMS/Collection settings.',
      },
      {
        match: 'insertTargetElementId is missing',
        message:
          'Please specify which element to update when using replace mode.',
      },
      {
        match: 'No HTML or CSS provided',
        message: 'No content was provided to render.',
      },
      {
        match: 'Cannot replace the root element',
        message:
          'The root element cannot be replaced. Try targeting a different element.',
      },
      {
        match: 'No node id provided',
        message: 'No insertion target is available on this page.',
        aiInstruction:
          'HARD STOP — Do NOT retry the same render call. Call read-page-tree once to get the current targetable element IDs. If the page has only Body, do not add raw page structure in Build mode; tell the user to restore/use a Page Slot with components or switch to Design mode. Otherwise, retry once with a valid target id, then stop.',
      },
      {
        match:
          'Raw page structure cannot be added directly under Body in Build mode',
        message:
          'Raw page structure cannot be added directly under Body in Build mode.',
        aiInstruction:
          'HARD STOP — Do NOT retry this Body insertion. Restore or use a Page Slot and add component instances inside it, or ask the user to switch to Design mode.',
      },
      {
        match: 'before or after the body element',
        message: 'Cannot insert relative to the page body.',
        aiInstruction:
          'You targeted the page <body> with insertMode before/after, but the body cannot have siblings. Use insertMode "append" (end of body) or "prepend" (start of body), or omit insertTargetElementId to append to the body. Do NOT retry before/after on the body.',
      },
      {
        match: '@keyframes animations are not allowed',
        message: 'CSS animations are not supported. Please remove @keyframes.',
        aiInstruction:
          'Webflow render does not support @keyframes/animations. Remove the @keyframes block and any animation-* properties, then retry. Do NOT resend the same CSS.',
      },
      {
        match: 'Nested selectors are not allowed',
        message:
          'CSS nested selectors are not supported. Use single class selectors only.',
        aiInstruction:
          'Only single-class selectors are supported (e.g. ".card-title"). Rewrite each rule to target one class, remove descendant/compound selectors, then retry. Do NOT resend the nested selector.',
      },
      {
        match: '::before and ::after pseudo-elements are not allowed',
        message:
          '::before and ::after pseudo-elements are not supported in Webflow.',
        aiInstruction:
          'Pseudo-elements (::before/::after) are not supported. Remove them and use real elements for the effect, then retry.',
      },
      {
        match: 'is not allowed. Only :hover',
        message:
          'Only :hover, :focus, and :active pseudo-classes are supported.',
        aiInstruction:
          'Only :hover, :focus, and :active are supported. Remove the unsupported pseudo-class (e.g. :last-of-type, :nth-child) and target a real class instead, then retry. Do NOT resend the same selector.',
      },
      {
        match: 'Updating CMS Collections is not yet supported',
        message: 'CMS Collection content cannot be edited with render.',
        aiInstruction:
          'This element is bound to a CMS Collection (data-w-collection-id / data-w-collection-source). Editing CMS-bound content via render is not supported. Do NOT retry. Tell the user this content is CMS-driven and must be edited in CMS/Collection settings.',
      },
      ...RENDER_NESTING_ERROR_PATTERNS,
    ],
  },
  llmTool: {
    name: toolName,
    description: dedent`
      Renders HTML (with optional CSS) into the Webflow Designer DOM. Supports plain HTML elements and Webflow component instances. Use render-css instead when only updating styles.

      # Restrictions
      - No CSS animations (@keyframes or animation properties)
      - No JavaScript (<script> tags or inline event handlers)
      - insertTargetElementId is required for "replace" and "remove" modes — get element IDs from read-page-tree
      ${emptyPageGuidance}
      - **PRESERVE ALL data-props-* ATTRIBUTES** — When editing an existing native element (one with data-w-short-id and data-props-* attributes), you MUST copy ALL data-props-* attributes from the original HTML into your output. Only change the values you need to update. Omitting any data-props-* attribute will RESET that property to its default and BREAK the element.

      # Insertion Mode
      Use insertTargetElementId (from read-page-tree) to position content precisely:
      - **before** / **after**: insert as a *sibling* before or after the target element (preferred for positioning near components)
      - **prepend** / **append**: insert as a *child* inside the target — ONLY use on plain containers (Body, Div), NEVER on components
      - **replace**: swap out the target element entirely (requires insertTargetElementId)
      - **remove**: delete the target element (requires insertTargetElementId)

      # Element IDs after rendering
      Do not reuse stale IDs after rendering plain HTML:
      - New plain HTML gets new IDs that are not returned; re-read before targeting it.
      - Replace keeps \`insertTargetElementId\`, but rebuilt child IDs can change; re-read before targeting children.
      - Remove deletes the target ID.
      - Only include \`data-w-id\` when intentionally preserving an existing element or component from a read result.
      - Only \`data-w-id\` values are targetable element IDs. Never use values from \`data-w-instance-of\`, \`data-w-component-id\`, \`w-node-*\` ids/classes, \`data-props-*\` payloads, or \`data-w-collection-*\` attributes as element IDs.

      # HTML Structure
      - Wrap all text in \`<h1>\`–\`<h6>\` or \`<p>\`. Use \`<div>\`/\`<span>\` only for layout/decoration.
        - Correct: \`<p class="blurb">text</p>\`
        - Wrong: \`<div class="blurb">...<span>...</span></div>\`
      - Lists: use \`<div>\` instead of \`<ul>\`/\`<ol>\`/\`<li>\`.
      - Buttons/links: \`<a>\` with text only, no nested elements.
      - Single class per element (e.g. \`class="card-header"\`). No nested CSS selectors.
      - Icons: use Lucide SVG HTML; control size via width, height, and viewBox (defaults: 24×24).

      # Element Nesting Constraints

      Webflow enforces strict parent-child rules for native elements. Do NOT insert arbitrary children into these:

      - **Webflow List component**: Existing Webflow List elements only accept ListItem children — you cannot insert \`<div>\` content into them, and \`<li>\` tags are also not supported. **Never use append/prepend targeting a Webflow List.** If the user asks to add an entry to a list, use insertMode \`"after"\` with the **List element's own ID** as \`insertTargetElementId\` — this places the new content as a sibling after the List, inside its parent container. Tell the user the content was placed after the list because native Lists do not support rich multi-element items. When generating fresh HTML from scratch, continue using \`<div>\` instead of \`<ul>\`/\`<ol>\`/\`<li>\`.
      - **Tab Links**: Leaf/anchor nodes — cannot have children. To add content inside a tab, target the Tab Pane element instead.
      - **Tabs Menu**: Only accepts Tab Link children. To add content to a tab, target the corresponding Tab Pane element inside Tabs Content instead.
      - **Slider elements** (labeled "Slider" in the designer): Fixed internal structure — target a Slider Slide (inside the Slider Mask) to add content, not the Slider element itself.
      - **Form Block elements**: Fixed internal structure — do not add arbitrary children to form containers; target existing field wrappers inside instead.
      - **Page Slots** (Navigator label "Page Slot"): Only \`<wf-component>\` children are allowed — never plain HTML. Never use \`remove\` or \`replace\` on the Page Slot itself. For a generated section, use \`create_reusable_section\` with placement targeting the Page Slot; for an existing component, use INSERT. Reorder with \`move_element\` or a single-render PRESERVE rewrite of the Page Slot children — never \`remove\` then PRESERVE.
      - **Collection Lists / CMS-bound elements**: If the target element or any HTML you read from the page contains \`data-w-collection-id\` or \`data-w-collection-source\`, do NOT use \`render\` with \`replace\`, \`remove\`, \`append\`, or \`prepend\` on that element or anything inside its subtree — it will fail with a CMS error. Instead choose the right alternative:
        - **CMS content** (text, images, fields): content is driven by CMS Collection data. Do not attempt to render into it — tell the user to edit the value in the CMS item or Collection field directly.
        - **Style-only changes**: use \`render-css\` targeting the existing class if the request can be satisfied without changing CMS structure or field values.
        - **Layout around a Collection List**: target a non-CMS wrapper or sibling element that sits outside the CMS-bound subtree.

      When you encounter a nesting error, do NOT retry the same approach. Instead:
      1. Use read-page-tree to identify the correct target element.
      2. Target a different parent (e.g., the parent of the constrained element).
      3. If the target is a component instance, use prop-* attributes instead of inserting children.

      # Component Instances

      Components appear as \`<wf-component>\` elements in read-page output. Never write component internals as raw HTML.

      **PRESERVE** — unchanged instance, \`data-w-id\` only:
      \`<wf-component data-w-id="abc123"></wf-component>\`

      **MODIFY** — update props on existing instance (\`data-w-id\` + changed \`prop-*\` attrs; omitted props unchanged):
      \`<wf-component data-w-id="abc123" prop-title="New Title"></wf-component>\`

      **INSERT** — new instance using \`component-id\` (the \`data-w-component-id\` value; use the 'list-file' tool to see what components are available for the site.):
      \`<wf-component component-id="card-comp-id" prop-title="Hello" prop-count="5"></wf-component>\`

      Property encoding:
      - Scalar: \`prop-kebab-name="value"\` — text as-is, numbers parsed, booleans "true"/"false"
      - Rich text: \`<wf-prop name="Field Name">HTML content</wf-prop>\` child element
      - Slot: \`<wf-slot name="Slot Name">...</wf-slot>\` — accepts \`<wf-component>\` instances or plain HTML children

      Rules:
      - ALWAYS use PRESERVE mode for existing instances you are not changing.
      - ALWAYS check read-page output for available prop names and types before setting them.
      - **Moving / reordering a component**: Prefer \`page-automation-public-mcp-compound-elements\` action \`move_element\` when available — true reparent; keeps props, bindings, and styles. Do NOT \`remove\` then PRESERVE: \`remove\` permanently deletes the instance from the page, and a later PRESERVE cannot resolve that \`data-w-id\`. If only \`render\` is available, keep the instances on the page and rewrite the parent/slot children in **one** render call, listing each as PRESERVE (\`<wf-component data-w-id="...">\` with no prop-* attrs) in the desired order. Never rebuild moved instances as plain HTML.
      - The \`component-id\` attribute must match the \`id\` field returned by the "list-file" tool. For site and library components this is a UUID (e.g. \`89353aaf-fb59-f0ec-7f26-144e41cc65a1\`); for code components it is a string name (e.g. \`helloworld\`). The "list-file" tool returns paths like \`<name>-<id>.wfdl\` — use only the \`id\` value, not the full filename.

      ## Component Guardrails — CRITICAL

      **NEVER remove, replace, or detach a \`<wf-component>\` instance to rebuild it as raw HTML.** Components are self-contained units. The only way to change a component instance is by updating its props (MODIFY mode). If a component does not look right, adjust its \`prop-*\` attributes — do NOT delete it and recreate its visual output with plain \`<div>\`/\`<p>\`/\`<a>\` elements.

      **NEVER use "remove" or "replace" mode targeting a \`<wf-component>\` element** unless the user explicitly asks to remove or replace that component. Moving/reordering is not a \`remove\` — use \`move_element\` or a single-render PRESERVE rewrite instead.

      **When a Slot (\`<wf-slot>\`) or Page Slot is selected**, and the content you want to edit is a \`<wf-component>\` instance, target that component's \`data-w-id\` for prop edits — do not attempt to edit, remove, or replace the slot element itself. **Page Slots only accept component instances: never insert plain HTML into a Page Slot.** To build a generated section in a Page Slot, use \`create_reusable_section\` with placement targeting the Page Slot; to insert an existing component, use INSERT mode (\`component-id\`) with the slot or an existing child as the \`insertTargetElementId\` anchor. Regular Slots accept plain HTML as well as component instances. To reorder \`<wf-component>\` instances inside a slot, prefer \`move_element\`, or rewrite the slot children in one PRESERVE render — never \`remove\` first, and never rebuild them as freeform HTML.

      **If the user asks to change content inside a component and no matching prop exists for that content, STOP and tell the user.** Do not attempt workarounds. Inform them that the element they want to edit is part of a component, and the component does not expose a prop for that content — only component props can be updated on instances. Internal elements are not directly editable.

      **Library components and code components are read-only.** Their internal structure can only be edited at the source site (library components) or in the source code (code components). You can insert or remove instances and set props via MODIFY mode, but you cannot modify their internal elements. If a user asks to change the internals, explain that the component must be edited at its source.

      **After successful placement.** If you need any element ID for a follow-up insert/modify/replace/remove, call \`read-page-tree\` (or \`read-page\`) first — new data-w-id values are not in the render response. Prefer \`read-page-tree\` when you need element names and IDs without full HTML verbosity. Do not re-read solely to inspect or "verify" \`<wf-component>\` internals; those tags are opaque references and a successful render already means placement succeeded.

      # Wireframes
      When given a wireframe or rough sketch, interpret the intent and produce a polished, production-ready result. Apply proper typography, spacing, colors, and visual hierarchy. Use real content from the page rather than placeholder text ("Heading", "Lorem ipsum"). Only match the rough aesthetic if the user explicitly asks.
    `,
    inputSchema: argumentZod,
  },
  type: 'designer',
  permission: [
    {resource: 'SiteStyleBlock', action: 'modify'},
    {resource: 'SitePage', action: 'editContent'},
  ],
  confirmBeforeExecuting: true,
  timeout: 30000,
});
